/**
 * FrsLeadWidget — framework-free branching lead form rendered inside a shadow
 * root. Drives the declarative step schema (schema.ts), handles conditional
 * step visibility, validation, and submission to the plugin REST webhook.
 */
import type { FormState, LoanType, Step, WidgetConfig, SubmitResult } from './types';
import { stepsFor } from './schema';
import { CSS } from './styles';
import { icon } from './icons';

const DEFAULT_CONSENT =
  'By proceeding, I consent to receive phone calls, SMS/text messages, and emails from the lender and its ' +
  'agents at the number and email I provided regarding products or services via automated or prerecorded means. ' +
  'Consent is not a condition of purchase. Reply STOP to cancel. Msg & data rates may apply.';

type Phase = 'chooser' | 'steps' | 'success';

export class FrsLeadWidget {
  private cfg: WidgetConfig;
  private root: ShadowRoot;
  private body!: HTMLElement;
  private state: FormState;
  private phase: Phase = 'steps';
  private currentId = '';
  private submitting = false;
  private consented = false;

  constructor(host: HTMLElement, cfg: WidgetConfig) {
    this.cfg = cfg;
    const initial: LoanType = cfg.loanType && cfg.loanType !== 'ask' ? cfg.loanType : 'purchase';
    this.state = { loanType: initial, values: { ...(cfg.meta || {}) } };
    this.phase = cfg.loanType === 'ask' ? 'chooser' : 'steps';

    this.root = host.shadowRoot || host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = CSS;
    this.root.appendChild(style);
    this.root.appendChild(this.buildShell());
    this.applyBrand();

    if (this.phase === 'steps') this.currentId = this.visibleSteps()[0].id;
    this.render();
  }

  /* ---------- shell ---------- */

  private buildShell(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'frs-llf';
    wrap.appendChild(this.el(this.headerHtml()));
    this.body = this.el('<main class="frs-body" id="frs-body"></main>');
    wrap.appendChild(this.body);
    wrap.appendChild(this.el(this.footerHtml()));
    wrap.addEventListener('click', (e) => this.onClick(e));
    wrap.addEventListener('submit', (e) => this.onSubmit(e));
    return wrap;
  }

  private applyBrand(): void {
    const b = this.cfg.brand || {};
    const host = this.root.host as HTMLElement;
    if (b.primaryColor) host.style.setProperty('--frs-primary', b.primaryColor);
    if (b.accentColor) host.style.setProperty('--frs-accent', b.accentColor);
  }

  private headerHtml(): string {
    const b = this.cfg.brand || {};
    const logo = b.logoUrl
      ? `<img class="frs-logo" src="${esc(b.logoUrl)}" alt="${esc(b.name || 'Home')}">`
      : `<span class="frs-logo-text">${esc(b.name || '21st Century Lending')}</span>`;
    const phone = b.phone
      ? `<a class="frs-phone" href="tel:${esc(b.phone.replace(/[^0-9+]/g, ''))}">${icon('phone')}<span>${esc(b.phone)}</span></a>`
      : '';
    return `<header class="frs-header">${logo}<div class="frs-header-right">${phone}</div></header>`;
  }

  private footerHtml(): string {
    const b = this.cfg.brand || {};
    const name = esc(b.name || '21st Century Lending');
    return `<footer class="frs-footer">© ${new Date().getFullYear()} ${name}. All rights reserved. Equal Housing Opportunity Lender.</footer>`;
  }

  /* ---------- step model ---------- */

  private visibleSteps(): Step[] {
    return stepsFor(this.state.loanType).filter((s) => !s.when || s.when(this.state));
  }

  private currentStep(): Step | undefined {
    return this.visibleSteps().find((s) => s.id === this.currentId);
  }

  private currentIndex(): number {
    return this.visibleSteps().findIndex((s) => s.id === this.currentId);
  }

  private isFinalStep(step: Step): boolean {
    const vis = this.visibleSteps();
    return vis[vis.length - 1]?.id === step.id;
  }

  /* ---------- navigation ---------- */

  private goNext(): void {
    const vis = this.visibleSteps();
    const idx = vis.findIndex((s) => s.id === this.currentId);
    const next = vis[idx + 1];
    if (next) {
      this.currentId = next.id;
      this.render();
    } else {
      void this.submit();
    }
  }

  private goBack(): void {
    if (this.phase === 'success') return;
    const vis = this.visibleSteps();
    const idx = vis.findIndex((s) => s.id === this.currentId);
    if (idx > 0) {
      this.currentId = vis[idx - 1].id;
      this.render();
    } else {
      // First step: always fall back to the loan-type chooser so the user can
      // change Purchase/Refinance even when they entered via a preset CTA.
      this.phase = 'chooser';
      this.render();
    }
  }

  private chooseLoanType(t: LoanType): void {
    this.state.loanType = t;
    this.phase = 'steps';
    this.currentId = this.visibleSteps()[0].id;
    this.render();
  }

  /* ---------- events ---------- */

  private onClick(e: Event): void {
    const target = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
    if (!target) return;
    const act = target.dataset.act;
    e.preventDefault();

    switch (act) {
      case 'choose-loan':
        this.chooseLoanType(target.dataset.value as LoanType);
        break;
      case 'choice': {
        const step = this.currentStep();
        if (!step) return;
        this.state.values[step.key] = target.dataset.value || '';
        // Loan-officer "yes" reveals the select instead of auto-advancing.
        if (step.kind === 'loanOfficer' && target.dataset.value === 'yes') {
          this.render();
        } else {
          this.goNext();
        }
        break;
      }
      case 'back':
        this.goBack();
        break;
      case 'skip': {
        // The loan-officer "Continue" reuses this action; capture the select value first.
        const sel = this.body.querySelector<HTMLSelectElement>('.frs-select[name="loan_officer_id"]');
        if (sel) this.state.values.loan_officer_id = sel.value;
        this.goNext();
        break;
      }
      case 'consent':
        this.consented = (target as HTMLInputElement).checked ?? !this.consented;
        break;
    }
  }

  private onSubmit(e: Event): void {
    e.preventDefault();
    const step = this.currentStep();
    if (!step || step.kind !== 'fields') return;
    if (!this.collectAndValidate(step)) return;
    if (this.isFinalStep(step) && !this.consented) {
      this.flashConsent();
      return;
    }
    this.goNext();
  }

  /* ---------- validation ---------- */

  private collectAndValidate(step: Step): boolean {
    let ok = true;
    for (const f of step.fields || []) {
      const input = this.body.querySelector<HTMLInputElement>(`[name="${f.key}"]`);
      if (!input) continue;
      const val = input.value.trim();
      const err = this.fieldError(f.type, val, f.required);
      this.setFieldError(input, err);
      if (err) ok = false;
      else this.state.values[f.key] = val;
    }
    return ok;
  }

  private fieldError(type: string, val: string, required?: boolean): string {
    if (!val) return required ? 'This field is required.' : '';
    if (type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return 'Enter a valid email address.';
    if (type === 'tel' && val.replace(/[^0-9]/g, '').length < 10) return 'Enter a valid phone number.';
    if (type === 'currency' && !/[0-9]/.test(val)) return 'Enter an amount.';
    return '';
  }

  private setFieldError(input: HTMLInputElement, err: string): void {
    input.classList.toggle('frs-invalid', Boolean(err));
    const holder = input.closest('.frs-field');
    let node = holder?.querySelector('.frs-input-error') as HTMLElement | null;
    if (err) {
      if (!node && holder) {
        node = this.el('<div class="frs-input-error"></div>');
        holder.appendChild(node);
      }
      if (node) node.textContent = err;
    } else if (node) {
      node.remove();
    }
  }

  private flashConsent(): void {
    const c = this.body.querySelector('.frs-consent label') as HTMLElement | null;
    if (c) {
      c.style.color = '#dc2626';
      setTimeout(() => (c.style.color = ''), 1600);
    }
  }

  /* ---------- submit ---------- */

  private async submit(): Promise<void> {
    if (this.submitting) return;
    this.submitting = true;
    this.render();
    const payload = {
      loan_type: this.state.loanType,
      ...this.state.values,
      tcpa_consent: this.consented ? 'yes' : '',
      page_url: location.href,
    };
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (this.cfg.nonce) headers['X-WP-Nonce'] = this.cfg.nonce;
      const res = await fetch(this.cfg.restUrl, { method: 'POST', headers, body: JSON.stringify(payload) });
      const data: SubmitResult = await res.json().catch(() => ({ ok: res.ok }));
      if (!res.ok || data.ok === false) throw new Error(data.message || 'Submission failed.');
      this.phase = 'success';
      this.submitting = false;
      this.render();
      this.cfg.onComplete?.(data);
    } catch (err) {
      this.submitting = false;
      this.render();
      const msg = this.body.querySelector('.frs-input-error');
      if (msg) msg.textContent = (err as Error).message || 'Something went wrong. Please try again.';
    }
  }

  /* ---------- rendering ---------- */

  private render(): void {
    if (this.phase === 'chooser') { this.body.innerHTML = this.chooserHtml(); return; }
    if (this.phase === 'success') { this.body.innerHTML = this.successHtml(); return; }
    const step = this.currentStep();
    if (!step) return;
    this.consented = this.consented && this.isFinalStep(step);
    this.body.innerHTML = this.stepHtml(step);
    const first = this.body.querySelector<HTMLInputElement>('.frs-input, .frs-select');
    first?.focus({ preventScroll: true });
  }

  private chooserHtml(): string {
    return `
      <h1 class="frs-question">What type of home loan are you looking for?</h1>
      <div class="frs-choices">
        <button class="frs-choice" data-act="choose-loan" data-value="purchase">${icon('house')}<span>Home Purchase</span></button>
        <button class="frs-choice" data-act="choose-loan" data-value="refinance">${icon('key-round')}<span>Home Refinance</span></button>
      </div>`;
  }

  private stepHtml(step: Step): string {
    const idx = this.currentIndex();
    const total = this.visibleSteps().length;
    const pct = Math.max(6, Math.round(((idx + 1) / (total + 1)) * 100));
    // Always offer Back: earlier steps step backward; the first step returns to
    // the loan-type chooser so users are never stuck without a way back.
    const back = `<button class="frs-back" data-act="back">${icon('chevron-left')} Back</button>`;
    const progress = `<div class="frs-progress"><div class="frs-progress-track"><div class="frs-progress-fill" style="width:${pct}%"></div></div><div class="frs-progress-label">${pct}%</div></div>`;
    const note = step.note ? `<p class="frs-note">${esc(step.note)}</p>` : '';
    const heading = `<h1 class="frs-question">${esc(step.question)}</h1>`;

    let content = '';
    if (step.kind === 'choice') content = this.choicesHtml(step);
    else if (step.kind === 'loanOfficer') content = this.loanOfficerHtml(step);
    else if (step.kind === 'fields') content = this.fieldsHtml(step);

    return `${back}${progress}${heading}${note}${content}`;
  }

  private choicesHtml(step: Step): string {
    const choices = step.choices || [];
    const isYesNo = choices.length === 2 && choices.every((c) => !c.icon);
    const current = this.state.values[step.key];
    const cards = choices
      .map((c) => {
        const ic = c.icon ? icon(c.icon) : '';
        const pressed = current === c.value ? 'true' : 'false';
        const cls = `frs-choice${isYesNo ? ' frs-yesno' : ''}`;
        return `<button class="${cls}" data-act="choice" data-value="${esc(c.value)}" aria-pressed="${pressed}">${ic}<span>${esc(c.label)}</span></button>`;
      })
      .join('');
    return isYesNo ? `<div class="frs-yesno-row">${cards}</div>` : `<div class="frs-choices">${cards}</div>`;
  }

  private loanOfficerHtml(step: Step): string {
    const yesno = this.choicesHtml(step);
    const showSelect = this.state.values[step.key] === 'yes' && (this.cfg.loanOfficers?.length || 0) > 0;
    const options = (this.cfg.loanOfficers || [])
      .map((lo) => `<option value="${esc(lo.id)}">${esc(lo.name)}${lo.nmls ? ` — NMLS #${esc(lo.nmls)}` : ''}</option>`)
      .join('');
    const select = showSelect
      ? `<div class="frs-lo-select"><select class="frs-select" name="loan_officer_id"><option value="">Select Loan Officer</option>${options}</select></div>`
      : '';
    const cont = showSelect
      ? `<div class="frs-actions"><button class="frs-continue" data-act="skip">Continue</button></div>`
      : `<button class="frs-skip" data-act="skip">Skip</button>`;
    return `${yesno}${select}${cont}`;
  }

  private fieldsHtml(step: Step): string {
    const final = this.isFinalStep(step);
    const rows = this.fieldInputs(step);
    const consent = final ? this.consentHtml() : '';
    const label = this.submitting
      ? `${icon('loader', 20, 'frs-spin')} ${final ? 'Submitting…' : 'Please wait…'}`
      : esc(step.continueLabel || 'Continue');
    const disabled = this.submitting ? 'disabled' : '';
    const btn = `<div class="frs-actions"><button type="submit" class="frs-continue" ${disabled}>${label}</button></div>`;
    return `<form class="frs-fields" novalidate>${rows}${consent}${btn}</form>`;
  }

  private fieldInputs(step: Step): string {
    const fields = step.fields || [];
    const inputs = fields.map((f) => this.oneInput(f));
    // Pair half-width fields (e.g. First/Last name) on a single row.
    if (fields.length === 2 && fields.every((f) => f.half)) {
      return `<div class="frs-field-row">${inputs.join('')}</div>`;
    }
    return inputs.join('');
  }

  private oneInput(f: { key: string; label: string; type: string; placeholder?: string; maxlength?: number; autocomplete?: string }): string {
    const val = esc(this.state.values[f.key] || '');
    const inputmode =
      f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : f.type === 'currency' || f.type === 'percent' || f.type === 'number' ? 'decimal' : 'text';
    const htmlType = f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : 'text';
    const max = f.maxlength ? `maxlength="${f.maxlength}"` : '';
    const ac = f.autocomplete ? `autocomplete="${esc(f.autocomplete)}"` : '';
    const inputEl = `<input class="frs-input" type="${htmlType}" inputmode="${inputmode}" name="${esc(f.key)}" value="${val}" placeholder="${esc(f.placeholder || '')}" ${max} ${ac}>`;
    const wrapped =
      f.type === 'currency'
        ? `<div class="frs-input-prefix"><span class="frs-prefix">$</span>${inputEl}</div>`
        : f.type === 'percent'
        ? `<div class="frs-input-prefix">${inputEl}<span class="frs-prefix" style="left:auto;right:16px">%</span></div>`
        : inputEl;
    return `<div class="frs-field"><label class="frs-label" for="frs-${esc(f.key)}">${esc(f.label)}</label>${wrapped}</div>`;
  }

  private consentHtml(): string {
    const text = esc((this.cfg.brand && (this.cfg.brand as { consentText?: string }).consentText) || DEFAULT_CONSENT);
    const checked = this.consented ? 'checked' : '';
    return `<div class="frs-consent"><input type="checkbox" id="frs-consent" data-act="consent" ${checked}><label for="frs-consent">${text}</label></div>`;
  }

  private successHtml(): string {
    return `
      <div class="frs-success">
        ${icon('circle-check', 64)}
        <h2>Thank you!</h2>
        <p>Your request has been received. A licensed loan officer will reach out shortly to walk you through your options.</p>
      </div>`;
  }

  /* ---------- utils ---------- */

  private el(html: string): HTMLElement {
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild as HTMLElement;
  }
}

function esc(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}
